import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { createCompanyWithSetup, suggestTemplates } from '../../reducers/companies';
import { MDBInput, MDBBtn } from 'mdbreact';
import '../../styles/page-common.css';
import './NewCompanyPage.css';

class NewCompanyPage extends Component {
  state = {
    step: 1,
    companyName: '',
    businessDescription: '',
    suggesting: false,
    suggestError: null,
    suggestions: null,
    enabledEntityTemplates: [],
    enabledAppointmentTypes: [],
    refinement: '',
    refining: false,
    creating: false,
  };

  // Step 1 → 2
  handleNext = () => {
    if (!this.state.companyName.trim()) return;
    this.setState({ step: 2, suggestError: null });
  };

  // Step 2: Generate
  handleGenerate = async () => {
    const { businessDescription } = this.state;
    if (!businessDescription.trim()) return;

    this.setState({ suggesting: true, suggestError: null });
    try {
      const result = await this.props.dispatchSuggestTemplates(businessDescription.trim());
      if (result && result.entityTemplates) {
        this.setState({
          suggestions: result,
          enabledEntityTemplates: result.entityTemplates.map(() => true),
          enabledAppointmentTypes: result.appointmentTypes.map(() => true),
          step: 3,
          suggesting: false,
        });
      } else {
        this.setState({ suggestError: 'Could not generate templates. Please try again.', suggesting: false });
      }
    } catch (e) {
      this.setState({ suggestError: 'Error generating templates. Please try again.', suggesting: false });
    }
  };

  // Step 2: Skip
  handleSkip = () => this.handleCreate(true);

  // Step 3: Refine
  handleRefine = async () => {
    const { businessDescription, suggestions, refinement } = this.state;
    if (!refinement.trim()) return;
    this.setState({ refining: true });
    try {
      const result = await this.props.dispatchSuggestTemplates(
        businessDescription.trim(), suggestions, refinement.trim()
      );
      if (result && result.entityTemplates) {
        this.setState({
          suggestions: result,
          enabledEntityTemplates: result.entityTemplates.map(() => true),
          enabledAppointmentTypes: result.appointmentTypes.map(() => true),
          refinement: '',
          refining: false,
        });
      } else {
        this.setState({ refining: false });
      }
    } catch (e) {
      this.setState({ refining: false });
    }
  };

  // Create company
  handleCreate = async (skipTemplates = false) => {
    const { companyName, suggestions, enabledEntityTemplates, enabledAppointmentTypes } = this.state;
    const company = { companyName: companyName.trim() };
    let templates = null;

    if (!skipTemplates && suggestions) {
      templates = {
        entityTemplates: suggestions.entityTemplates.filter((_, i) => enabledEntityTemplates[i]),
        invoiceTemplate: suggestions.invoiceTemplate,
        appointmentTypes: suggestions.appointmentTypes.filter((_, i) => enabledAppointmentTypes[i]),
      };
    }

    this.setState({ creating: true });
    try {
      await this.props.dispatchCreateCompanyWithSetup(company, templates);
      this.props.history.push('/');
    } catch (e) {
      this.setState({ creating: false });
    }
  };

  toggleEntityTemplate = (i) => {
    const enabled = [...this.state.enabledEntityTemplates];
    enabled[i] = !enabled[i];
    this.setState({ enabledEntityTemplates: enabled });
  };

  toggleAppointmentType = (i) => {
    const enabled = [...this.state.enabledAppointmentTypes];
    enabled[i] = !enabled[i];
    this.setState({ enabledAppointmentTypes: enabled });
  };

  renderProgressBar() {
    const { step } = this.state;
    const labels = ['Company Name', 'Describe Business', 'Review Templates'];
    return (
      <div className="ncp-progress">
        {labels.map((label, i) => (
          <div key={i} className={`ncp-progress-step ${step > i + 1 ? 'done' : ''} ${step === i + 1 ? 'active' : ''}`}>
            <div className="ncp-progress-dot">
              {step > i + 1 ? <i className="fas fa-check"></i> : i + 1}
            </div>
            <span>{label}</span>
          </div>
        ))}
      </div>
    );
  }

  renderStep1() {
    const { companyName } = this.state;
    return (
      <div className="ncp-card">
        <h2>What's your company called?</h2>
        <p className="text-muted">This is how it'll appear on invoices and to your clients.</p>
        <MDBInput
          label="Company Name"
          value={companyName}
          onChange={e => this.setState({ companyName: e.target.value })}
          onKeyPress={e => e.key === 'Enter' && this.handleNext()}
          autoFocus
        />
        <div className="ncp-actions">
          <Link to="/" className="btn btn-outline-secondary">Cancel</Link>
          <MDBBtn color="primary" onClick={this.handleNext} disabled={!companyName.trim()}>
            Next <i className="fas fa-arrow-right ml-1"></i>
          </MDBBtn>
        </div>
      </div>
    );
  }

  renderStep2() {
    const { companyName, businessDescription, suggesting, suggestError } = this.state;
    return (
      <div className="ncp-card">
        <h2>Tell us about "{companyName}"</h2>
        <p className="text-muted">
          Describe what your business does and we'll use AI to set up the perfect templates for you — 
          client fields, appointment types, invoice layout, and more.
        </p>
        <MDBInput
          type="textarea"
          label="What type of business is this?"
          rows="4"
          value={businessDescription}
          onChange={e => this.setState({ businessDescription: e.target.value })}
          hint="e.g. veterinary practice that treats dogs and cats"
        />
        {suggestError && <p className="text-danger mt-2">{suggestError}</p>}
        <div className="ncp-actions">
          <MDBBtn color="secondary" onClick={() => this.setState({ step: 1 })}>
            <i className="fas fa-arrow-left mr-1"></i> Back
          </MDBBtn>
          <div>
            <MDBBtn color="default" onClick={this.handleSkip} disabled={suggesting}>
              Skip — no templates
            </MDBBtn>
            <MDBBtn
              color="primary"
              onClick={this.handleGenerate}
              disabled={!businessDescription.trim() || suggesting}
            >
              {suggesting ? (
                <><i className="fas fa-spinner fa-spin mr-1"></i> Generating...</>
              ) : (
                <><i className="fas fa-magic mr-1"></i> Generate Templates</>
              )}
            </MDBBtn>
          </div>
        </div>
      </div>
    );
  }

  renderStep3() {
    const {
      suggestions, enabledEntityTemplates, enabledAppointmentTypes,
      refinement, refining, creating, companyName
    } = this.state;

    if (!suggestions) return null;
    const { entityTemplates, invoiceTemplate, appointmentTypes } = suggestions;
    const hasEnabled = enabledEntityTemplates.some(Boolean) || enabledAppointmentTypes.some(Boolean);

    return (
      <div className="ncp-card ncp-card-wide">
        <h2>Review Templates for "{companyName}"</h2>
        <p className="text-muted">Select which templates to create. You can always add more later.</p>

        <div className="ncp-templates">
          {/* Entity Templates */}
          {entityTemplates.length > 0 && (
            <div className="ncp-template-group">
              <h5><i className="fas fa-users mr-2"></i>Client & Entity Templates</h5>
              {entityTemplates.map((et, i) => (
                <div
                  key={i}
                  className={`ncp-template-item ${enabledEntityTemplates[i] ? 'selected' : ''}`}
                  onClick={() => this.toggleEntityTemplate(i)}
                >
                  <input type="checkbox" checked={enabledEntityTemplates[i]} readOnly />
                  <div>
                    <strong>{et.reviewName || et.name || `Template ${i + 1}`}</strong>
                    {et.secondary && <span className="badge badge-secondary ml-2">Secondary</span>}
                    <div className="text-muted small mt-1">
                      {et.reviewDescription || `${(et.descriptions || []).length} fields`}
                    </div>
                    <div className="text-muted small">
                      Fields: {(et.descriptions || []).map(d => d.description).join(', ')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Invoice Template */}
          {invoiceTemplate && (
            <div className="ncp-template-group">
              <h5><i className="fas fa-file-invoice-dollar mr-2"></i>Invoice Template</h5>
              <div className="ncp-template-item selected always">
                <span className="text-success mr-2">✓</span>
                <div>
                  <strong>{invoiceTemplate.templateName || 'Standard Invoice'}</strong>
                  <div className="text-muted small mt-1">Always included</div>
                </div>
              </div>
            </div>
          )}

          {/* Appointment Types */}
          {appointmentTypes.length > 0 && (
            <div className="ncp-template-group">
              <h5><i className="fas fa-calendar-alt mr-2"></i>Appointment Types</h5>
              {appointmentTypes.map((apt, i) => (
                <div
                  key={i}
                  className={`ncp-template-item ${enabledAppointmentTypes[i] ? 'selected' : ''}`}
                  onClick={() => this.toggleAppointmentType(i)}
                >
                  <input type="checkbox" checked={enabledAppointmentTypes[i]} readOnly />
                  <div>
                    <strong>{apt.name}</strong>
                    <div className="text-muted small mt-1">
                      {apt.reviewDescription || apt.description || ''}
                    </div>
                    <div className="text-muted small">
                      Statuses: {(apt.statuses || []).map(s => s.name).join(', ')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Refinement */}
          <div className="ncp-refine">
            <h5><i className="fas fa-plus-circle mr-2"></i>Need something else?</h5>
            <div className="d-flex align-items-center">
              <MDBInput
                label="Describe what to add..."
                value={refinement}
                onChange={e => this.setState({ refinement: e.target.value })}
                className="flex-grow-1 mb-0"
                onKeyPress={e => e.key === 'Enter' && this.handleRefine()}
              />
              <MDBBtn
                color="default" size="sm" className="ml-2"
                onClick={this.handleRefine}
                disabled={!refinement.trim() || refining}
                style={{ marginTop: '-8px', whiteSpace: 'nowrap' }}
              >
                {refining ? '...' : 'Add'}
              </MDBBtn>
            </div>
          </div>
        </div>

        <div className="ncp-actions">
          <MDBBtn color="secondary" onClick={() => this.setState({ step: 2 })}>
            <i className="fas fa-arrow-left mr-1"></i> Back
          </MDBBtn>
          <MDBBtn
            color="primary"
            onClick={() => this.handleCreate(false)}
            disabled={creating}
          >
            {creating ? (
              <><i className="fas fa-spinner fa-spin mr-1"></i> Creating...</>
            ) : (
              <><i className="fas fa-check mr-1"></i> Create{hasEnabled ? ' with Templates' : ''}</>
            )}
          </MDBBtn>
        </div>
      </div>
    );
  }

  render() {
    const { step } = this.state;
    return (
      <div className="content-wrapper">
        <div className="ncp-container">
          {this.renderProgressBar()}
          {step === 1 && this.renderStep1()}
          {step === 2 && this.renderStep2()}
          {step === 3 && this.renderStep3()}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  user: state.auth.user,
});

const mapDispatchToProps = (dispatch) => ({
  dispatchCreateCompanyWithSetup: (company, templates) => dispatch(createCompanyWithSetup(company, templates)),
  dispatchSuggestTemplates: (description, existing, refinement) => dispatch(suggestTemplates(description, existing, refinement)),
});

export default connect(mapStateToProps, mapDispatchToProps)(NewCompanyPage);
